import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import { AxiosInterceptor } from '../Config/AxiosInterceptor'
import {
  ApiResponse,
  BlogListItem,
  BlogSingleResponse,
  BlogTaxonomy,
  CreateBlogPayload,
  PaginatedResponse,
  RestoreBlogPayload,
  UpdateBlogPayload,
  UploadedImage,
} from 'Models'

AxiosInterceptor()

export interface BlogListParams {
  page?: number
  pageSize?: number
  status?: 'draft' | 'published'
  category?: string
  tag?: string
  search?: string
  deleted?: boolean
}

/**
 * Builds the query string for the admin list endpoint from whichever
 * filters are actually set
 */
const buildListQuery = (params: BlogListParams): string => {
  const query = new URLSearchParams()
  query.set('page', String(params.page ?? 1))
  query.set('pageSize', String(params.pageSize ?? 10))
  if (params.status) query.set('status', params.status)
  if (params.category) query.set('category', params.category)
  if (params.tag) query.set('tag', params.tag)
  if (params.search) query.set('search', params.search)
  if (params.deleted) query.set('deleted', 'true')
  return query.toString()
}

const blogService = {
  getAll: async (
    params: BlogListParams = {},
  ): Promise<AxiosResponse<ApiResponse<PaginatedResponse<BlogListItem>>>> => {
    return axios.get(`/tradegridplusblog?${buildListQuery(params)}`)
  },

  getSingle: async (
    slug: string,
    options: { includeDeleted?: boolean } = {},
  ): Promise<AxiosResponse<ApiResponse<BlogSingleResponse>>> => {
    const query = options.includeDeleted ? '?includeDeleted=true' : ''
    return axios.get(`/tradegridplusblog/${encodeURIComponent(slug)}${query}`)
  },

  create: async (
    data: CreateBlogPayload,
  ): Promise<AxiosResponse<ApiResponse<BlogSingleResponse>>> => {
    return axios.post('/tradegridplusblog', data)
  },

  update: async (
    slug: string,
    data: UpdateBlogPayload,
  ): Promise<AxiosResponse<ApiResponse<BlogSingleResponse>>> => {
    return axios.put(`/tradegridplusblog/${encodeURIComponent(slug)}`, data)
  },

  trash: async (
    slug: string,
    version: number,
  ): Promise<AxiosResponse<ApiResponse<{ message: string }>>> => {
    return axios.delete(`/tradegridplusblog/${encodeURIComponent(slug)}?version=${version}`)
  },

  restore: async (
    slug: string,
    data: RestoreBlogPayload,
  ): Promise<AxiosResponse<ApiResponse<BlogSingleResponse>>> => {
    return axios.post(`/tradegridplusblog/${encodeURIComponent(slug)}/restore`, data)
  },

  file: async (
    formData: FormData,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<ApiResponse<UploadedImage>>> => {
    return axios.post('/tradegridplusblog/images', formData, {
      ...config,
      headers: {
        ...config?.headers,
        'Content-Type': undefined,
      },
    })
  },
  getPublicTaxonomy: async (): Promise<AxiosResponse<ApiResponse<BlogTaxonomy>>> => {
    return axios.get('/tradegridplusblog/public/taxonomy')
  },
}

export default blogService
